import { useState, useCallback, useEffect } from 'react';
import { api } from '@halo/web-lib';
import { useIngestionSSE } from '../../hooks/useIngestionSSE';
import {
  Layers,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  CircleDot,
  AlertTriangle,
  Clock,
  Rows3,
  Calendar,
  Loader2,
} from 'lucide-react';
import type { BackfillGroup } from './types';
import { STATUS_CONFIG, formatDuration, formatMonthLabel } from './types';
import {
  CLOUD_RUN_JOB_HEAVY_THRESHOLD_MONTHS,
  cloudRunJobExecutionUrl,
  type CloudRunJobBackfillMeta,
} from './cloud-run-jobs-types';

/**
 * Intersection of the shared BackfillGroup shape with an optional Cloud Run
 * Jobs marker. The marker fields aren't currently persisted server-side, so
 * `via`/`executionName` are absent in practice — we infer the dispatch path
 * from `jobCount` instead. The type still allows future enrichment without
 * touching the shared `types.ts` (concurrent edits in progress).
 */
type BackfillGroupWithCloudRun = BackfillGroup & CloudRunJobBackfillMeta;

function isCloudRunJobDispatch(group: BackfillGroupWithCloudRun): boolean {
  // Prefer explicit marker if the API ever starts returning it.
  if (group.via === 'cloud-run-jobs') return true;
  // Fallback heuristic: heavy backfills (>= threshold months) are routed
  // through Cloud Run Jobs by the backend dispatcher.
  return group.jobCount >= CLOUD_RUN_JOB_HEAVY_THRESHOLD_MONTHS;
}

export function BackfillGroupsTab({ storeId, platform }: { storeId: number; platform: string }) {
  const [groups, setGroups] = useState<BackfillGroupWithCloudRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const fetchGroups = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/ingestion/admin/backfill-groups?limit=50');
      if (res.success && Array.isArray(res.data)) {
        // Filter to this store+platform
        const filtered = (res.data as BackfillGroupWithCloudRun[]).filter(
          (g) => g.storeId === storeId && g.platform === platform,
        );
        setGroups(filtered);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [storeId, platform]);

  useEffect(() => {
    fetchGroups();
    // Slow fallback poll (60s) — SSE handles real-time updates
    const interval = setInterval(fetchGroups, 60_000);
    return () => clearInterval(interval);
  }, [fetchGroups]);

  // SSE real-time updates
  useIngestionSSE({
    storeId,
    platform,
    onEvent: useCallback(() => {
      fetchGroups();
    }, [fetchGroups]),
  });

  const toggleExpand = (groupId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 text-gray-400 dark:text-gray-500 animate-spin" />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center">
        <Layers className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500 dark:text-gray-400">No backfill groups yet</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Trigger a backfill to see grouped job progress here</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {groups.map((group) => {
        const isOpen = expanded.has(group.groupId);
        const gs = STATUS_CONFIG[group.status] ?? STATUS_CONFIG['completed']!;
        const GroupIcon = gs.icon;

        const progressPct = group.totalWindows > 0
          ? Math.round((group.completedWindows / group.totalWindows) * 100)
          : 0;

        const isCloudRunJob = isCloudRunJobDispatch(group);
        const executionUrl = group.executionName
          ? cloudRunJobExecutionUrl(group.executionName)
          : null;

        return (
          <div key={group.groupId} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            {/* Group Header */}
            <button
              onClick={() => toggleExpand(group.groupId)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-950 transition-colors text-left"
            >
              <div className="flex-shrink-0">
                {isOpen
                  ? <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  : <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                }
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {group.jobCount}-month backfill
                  </span>
                  {(group.retries ?? 0) > 0 && (
                    <span className="text-[10px] text-amber-500 font-normal">
                      ({group.retries} {group.retries === 1 ? 'retry' : 'retries'})
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${gs.className}`}>
                    <GroupIcon className="w-3 h-3" />
                    {gs.label}
                  </span>
                  {isCloudRunJob && (
                    <span
                      title={
                        group.executionName
                          ? `Cloud Run Job execution: ${group.executionName}`
                          : 'Heavy backfill — dispatched via Cloud Run Jobs (inferred from window size)'
                      }
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300"
                    >
                      cloud-run-job
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-3 text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {group.dateRange.start?.slice(0, 7) ?? '?'} → {group.dateRange.end?.slice(0, 7) ?? '?'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Rows3 className="w-3 h-3" />
                    {group.totalRows.toLocaleString()} rows
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDuration(group.durationMs)}
                  </span>
                  <span>
                    {group.completed}/{group.jobCount} months done
                  </span>
                </div>
              </div>

              {/* Mini progress bar */}
              {group.status === 'running' && group.totalWindows > 0 && (
                <div className="w-20 flex-shrink-0">
                  <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-1.5">
                    <div
                      className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <div className="text-[9px] text-gray-400 dark:text-gray-500 text-right mt-0.5">{progressPct}%</div>
                </div>
              )}
            </button>

            {/* Expanded: month-level breakdown */}
            {isOpen && (
              <div className="border-t border-gray-100 dark:border-gray-800">
                <div className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-x-4 gap-y-0 px-4 py-1 text-[10px] text-gray-400 dark:text-gray-500 font-medium border-b border-gray-50 bg-gray-50 dark:bg-gray-950">
                  <span />
                  <span>Month</span>
                  <span className="text-right">Windows</span>
                  <span className="text-right">Rows</span>
                  <span className="text-right">Status</span>
                </div>
                <div className="divide-y divide-gray-50">
                  {group.months.map((m, i) => {
                    const monthLabel = m.month ? formatMonthLabel(m.month) : `Job ${i + 1}`;
                    const ms = STATUS_CONFIG[m.status] ?? STATUS_CONFIG['completed']!;
                    const MonthIcon = ms.icon;
                    const windowProgress = m.windowsTotal > 0
                      ? `${m.windowsCompleted}/${m.windowsTotal}`
                      : '—';

                    return (
                      <div key={i} className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-x-4 items-center px-4 py-2 text-xs">
                        <div className="flex-shrink-0">
                          {m.status === 'completed' ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                          ) : m.status === 'errored' ? (
                            <XCircle className="w-3.5 h-3.5 text-red-500" />
                          ) : m.status === 'running' ? (
                            <CircleDot className="w-3.5 h-3.5 text-blue-500 animate-pulse" />
                          ) : (
                            <CircleDot className="w-3.5 h-3.5 text-gray-300" />
                          )}
                        </div>
                        <span className="text-gray-700 dark:text-gray-200 font-medium">{monthLabel}</span>
                        <span className="text-right text-gray-500 dark:text-gray-400 font-mono text-[10px]">{windowProgress}</span>
                        <span className="text-right text-gray-500 dark:text-gray-400 tabular-nums">{m.rowsIngested.toLocaleString()}</span>
                        <div className="flex justify-end">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium ${ms.className}`}>
                            {ms.label}
                          </span>
                        </div>
                        {m.errorMessage && (
                          <div className="col-span-5 text-[10px] text-red-500 truncate pl-7 -mt-1 pb-1">
                            {m.errorMessage}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Group footer summary */}
                <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800 text-[10px] text-gray-500 dark:text-gray-400">
                  <span className="font-mono">{group.groupId.slice(0, 20)}…</span>
                  <div className="flex items-center gap-3">
                    {executionUrl && (
                      <a
                        href={executionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-violet-600 hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-200 underline-offset-2 hover:underline"
                      >
                        View Cloud Run Job execution
                      </a>
                    )}
                    {group.errored > 0 && (
                      <span className="flex items-center gap-1 text-red-500">
                        <AlertTriangle className="w-3 h-3" /> {group.errored} failed
                      </span>
                    )}
                    <span>{group.completedWindows}/{group.totalWindows} windows</span>
                    <span>{formatDuration(group.durationMs)} total</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
